/* eslint-disable @angular-eslint/prefer-inject */
import { ReceiptHeaderDataService } from '../infrastructure/receipt-headers/receipt-header.data.service';
import {
    ReceiptHeaderCreate,
    ReceiptHeaderDto,
    ReceiptHeaderRead,
    ReceiptHeaderSearchParams,
} from '../entities/receipt-header.dto';
import { ReceiptHeaderStore } from '../infrastructure/receipt-headers/receipt-header.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { ReceiptHeaderMapper } from '../infrastructure/receipt-headers/receipt-header.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { IManageEntityRoutes, IQueryParams, SafeAny } from '@pf/shared-common';
import { map, Observable, of, switchMap } from 'rxjs';
import { WarehouseFacade } from './warehouse.facade';
import { ResourceAuth } from '@control-tower/platform-wms';
import { ReceiptLineStore } from '../infrastructure/receipt-lines/receipt-line.store';
import { ReceiptLineMapper } from '../infrastructure/receipt-lines/receipt-line.mapper';
import { ReceiptLineRead } from '../entities/receipt-line.dto';

@Injectable()
export class ReceiptHeaderFacade extends AbstractManageEntityFacade<
    ReceiptHeaderDto,
    ReceiptHeaderRead,
    ReceiptHeaderCreate,
    ReceiptHeaderSearchParams
> {
    private readonly receiptLineStore = inject(ReceiptLineStore);
    private readonly receiptLineMapper = inject(ReceiptLineMapper);
    private readonly warehouseFacade = inject(WarehouseFacade);
    nameField: keyof ReceiptHeaderDto = 'poNumber';

    constructor(
        dataService: ReceiptHeaderDataService,
        store: ReceiptHeaderStore,
        mapper: ReceiptHeaderMapper
    ) {
        super(
            dataService,
            null as unknown as IManageEntityRoutes,
            store,
            mapper
        );
    }

    override getDefaultSearchParams$(
        params: IQueryParams<ReceiptHeaderDto>
    ): Observable<IQueryParams<ReceiptHeaderDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: ReceiptHeaderCreate) {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof ReceiptHeaderDto, string>> {
        return { poNumber: searchText };
    }

    getByPoNumber$(poNumber: string): Observable<ReceiptHeaderDto[]> {
        return this.searchAll$({
            pageIndex: 1,
            pageSize: 50,
            filters: {
                poNumber,
            } as SafeAny,
        }).pipe(
            switchMap(() => {
                return this.store.filter$((receipt: ReceiptHeaderRead) => {
                    return receipt.poNumber === poNumber;
                });
            }),
            map((receipts: ReceiptHeaderRead[]) => {
                return this.mapper.toDTOList(receipts);
            }),
            switchMap(receipts => {
                if (receipts.length === 0) {
                    return of([]);
                }
                const receiptIds = receipts.map(r => r.id);
                return this.receiptLineStore
                    .filter$((receiptLine: ReceiptLineRead) => {
                        return (
                            receiptLine.inventoryReceiptId &&
                            receiptIds.includes(receiptLine.inventoryReceiptId)
                        );
                    })
                    .pipe(
                        map((receiptLines: ReceiptLineRead[]) => {
                            return this.receiptLineMapper.toDTOList(
                                receiptLines
                            );
                        }),
                        map(receiptLines => {
                            return receipts.map(receipt => {
                                receipt.receiptLines = receiptLines.filter(
                                    receiptLine =>
                                        receiptLine.inventoryReceiptId ===
                                        receipt.id
                                );
                                return receipt;
                            });
                        })
                    );
            })
        );
    }

    receiveLines$(
        headerId: string,
        lineIds: string[],
        receivingContainerId: string
    ) {
        return (this.dataService as ReceiptHeaderDataService).receiveLines$(
            headerId,
            lineIds,
            receivingContainerId
        );
    }
}
